import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { COLOR_TOKENS } from '../designSystem/colors/colors';
import { AppIcon } from '../designSystem/icons/AppIcon';

const MAX_LEFT_IMAGE_DIMENSION = 232;

const HIGHLIGHTED_CTA_BACKGROUND_TYPE = {
  GRID: 'GRID',
  LINED: 'LINED',
  SOLID: 'SOLID',
} as const;

type HighlightedCTABackgroundType =
  (typeof HIGHLIGHTED_CTA_BACKGROUND_TYPE)[keyof typeof HIGHLIGHTED_CTA_BACKGROUND_TYPE];

type HighlightedCTACardProps = Readonly<{
  leftImageUrl: string;
  title: string;
  description: string;
  ctaText: string;
  backgroundType?:
    | {
        type: 'GRID' | 'LINED';
      }
    | { type: 'SOLID'; backgroundColor: string };
  ctaOnClick: () => void;
}>;

export const HighlightedCTACard = ({
  leftImageUrl,
  title,
  description,
  ctaText,
  backgroundType = { type: HIGHLIGHTED_CTA_BACKGROUND_TYPE.GRID },
  ctaOnClick,
}: HighlightedCTACardProps) => {
  const backgroundStyleToUse = backgroundTypeToStyleMap[backgroundType.type];

  const backgroundColor =
    backgroundType.type === HIGHLIGHTED_CTA_BACKGROUND_TYPE.SOLID
      ? backgroundType.backgroundColor
      : undefined;

  return (
    <Box sx={containerSx(backgroundColor)}>
      <Box component="img" src={leftImageUrl} sx={leftImageSx} />
      <Box sx={backgroundStyleToUse}>
        <Box sx={titleButtonContainerSx}>
          <Typography variant="display-xs-bold">{title}</Typography>
          <Button
            hierarchy="Primary"
            size="lg"
            onClick={ctaOnClick}
            endIcon={<AppIcon iconName="ArrowRight" diameter="20px" color="Colors/Base/White" />}
            sx={buttonSx}
          >
            {ctaText}
          </Button>
        </Box>
        <Box sx={descriptionSx}>
          <Typography variant="text-sm-regular">{description}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const containerSx =
  (backgroundColor?: string): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    borderRadius: backrs.radius['radius-xl'],
    overflow: 'hidden',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backgroundColor || backrs.colors['Colors/Base/White'],
  });

const leftImageSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: {
    sm: MAX_LEFT_IMAGE_DIMENSION,
  },
  maxHeight: {
    xs: MAX_LEFT_IMAGE_DIMENSION / 1.75,
    sm: 'unset',
  },
  borderRight: {
    sm: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  borderBottom: {
    xs: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    sm: 'none',
  },
  height: 'auto',
  borderTopLeftRadius: backrs.radius['radius-xl'],
  borderBottomLeftRadius: { md: backrs.radius['radius-xl'] },
  backgroundColor: backrs.colors['Colors/Background/bg-brand-primary'],
  pr: backrs.spacing['spacing-3xl'],
  pl: backrs.spacing['spacing-5xl'],
  py: {
    xs: backrs.spacing['spacing-3xl'],
    sm: backrs.spacing['spacing-6xl'],
  },
});

const titleButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: {
    xs: 'column-reverse',
    md: 'row',
  },
  justifyContent: 'space-between',
  alignItems: {
    xs: 'flex-start',
    md: 'center',
  },
  mb: backrs.spacing['spacing-2xl'],
  gap: backrs.spacing['spacing-xl'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  mr: { md: backrs.spacing['spacing-9xl'] },
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 'fit-content',
  marginLeft: {
    md: backrs.spacing['spacing-4xl'],
  },
  alignSelf: {
    xs: 'flex-start',
    sm: 'flex-end',
    md: 'flex-start',
  },
});

/**
 * Background styles
 * - GRID: grid background (like a graph paper)
 * - LINED: lined background (like a notebook)
 * - SOLID: solid background
 */
const HORIZONTAL_FADE_PERCENTAGE = 40; // horizontal fade area
const VERTICAL_FADE_PERCENTAGE = 85; // vertical fade area
const CENTER_OPACITY = 0.35; // opacity at top center
const GRID_SPACING_X = 32; // spacing between grid lines
const GRID_SPACING_Y = 48; // spacing between grid lines
const GRID_COLOR = COLOR_TOKENS['Brand/500']; // grid line color

const FADE_HORIZONTAL_POSITION = 50; // horizontal position of the fade
const FADE_START_PERCENTAGE = 0; // fade starts at the top center
const FADE_END_PERCENTAGE = 65; // fully faded out at 65%

const SHIFT_UP = 8; // shift the grid up to make the top fade out

const contentContainerCommon: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  p: backrs.spacing['spacing-3xl'],
});

const gridBackground: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  p: backrs.spacing['spacing-3xl'],
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -SHIFT_UP,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(${GRID_COLOR} 1px, transparent 1px),
      linear-gradient(to right, ${GRID_COLOR} 1px, transparent 1px)
    `,
    backgroundSize: `${GRID_SPACING_X}px ${GRID_SPACING_Y}px`,
    backgroundPosition: 'center',
    zIndex: 0,
    pointerEvents: 'none',

    WebkitMaskImage: `
      radial-gradient(
        ${HORIZONTAL_FADE_PERCENTAGE * 2}% ${VERTICAL_FADE_PERCENTAGE * 2}% at ${FADE_HORIZONTAL_POSITION}% 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) ${FADE_START_PERCENTAGE}%,
        rgba(0, 0, 0, 0) ${FADE_END_PERCENTAGE}%
      )
    `,
    maskImage: `
      radial-gradient(
        ${HORIZONTAL_FADE_PERCENTAGE * 2}% ${VERTICAL_FADE_PERCENTAGE * 2}% at ${FADE_HORIZONTAL_POSITION}% 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) ${FADE_START_PERCENTAGE}%,
        rgba(0, 0, 0, 0) ${FADE_END_PERCENTAGE}%
      )
    `,
  },

  zIndex: 1,
});

const linedBackground: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  p: backrs.spacing['spacing-3xl'],
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -SHIFT_UP,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
    linear-gradient(${GRID_COLOR} 1px, transparent 0px)
  `,
    backgroundSize: `100% ${GRID_SPACING_X + 4}px`,
    backgroundPosition: 'top left',

    zIndex: 0,
    pointerEvents: 'none',

    WebkitMaskImage: `
      radial-gradient(
        ${HORIZONTAL_FADE_PERCENTAGE * 2}% ${VERTICAL_FADE_PERCENTAGE * 2}% at ${FADE_HORIZONTAL_POSITION}% 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) ${FADE_START_PERCENTAGE}%,
        rgba(0, 0, 0, 0) ${FADE_END_PERCENTAGE}%
      )
    `,
    maskImage: `
      radial-gradient(
        ${HORIZONTAL_FADE_PERCENTAGE * 2}% ${VERTICAL_FADE_PERCENTAGE * 2}% at ${FADE_HORIZONTAL_POSITION}% 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) 0%,
        rgba(0, 0, 0, ${CENTER_OPACITY}) ${FADE_START_PERCENTAGE}%,
        rgba(0, 0, 0, 0) ${FADE_END_PERCENTAGE}%
      )
    `,
  },

  zIndex: 1,
});

const backgroundTypeToStyleMap: Record<HighlightedCTABackgroundType, SxProps<Theme>> = {
  GRID: gridBackground,
  LINED: linedBackground,
  SOLID: contentContainerCommon,
};
